<template>
  <!-- Login page (US 9383): email-first sign-in inside the shared sign-in layout -->
  <auth-layout>
    <!-- Messages that can show on any step -->
    <div v-if="successMessage" class="lg-alert lg-alert--teal" qid="login-form-success">
      <font-awesome-icon icon="info-circle"/><div>{{ successMessage }}</div>
    </div>
    <div v-if="showResendEmailSuccessMessage" class="lg-alert lg-alert--teal">
      <font-awesome-icon icon="envelope"/><div>{{ $t('resend_email.success_message') }}</div>
    </div>
    <div v-if="showTokenExpired" class="lg-alert lg-alert--red" qid="login-form-error">
      <font-awesome-icon icon="exclamation-circle"/>
      <div>
        {{ $t('login.token_expired_message') }}
        <a href="#" @click.prevent="resendEmail()">{{ $t('login.resend_email') }}</a>
      </div>
    </div>

    <!-- 1. Email -->
    <template v-if="step === 'email'">
      <h1 class="lg-title">{{ $t('login.title') }}</h1>
      <p class="lg-sub">{{ $t('login.email_intro') }}</p>
      <div v-if="genericError" class="lg-alert lg-alert--red" qid="login-form-error">
        <font-awesome-icon icon="exclamation-circle"/><div>{{ genericError }}</div>
      </div>
      <form @submit.prevent="continueWithEmail" qid="login-form" novalidate>
        <div class="lg-field">
          <label for="lg-email">{{ $t('login.work_email_label') }}</label>
          <div class="lg-input" :class="{ 'lg-input--err': emailInvalid }">
            <font-awesome-icon icon="envelope"/>
            <input id="lg-email"
                   ref="email"
                   type="email"
                   v-model.trim="form.email"
                   autocomplete="username"
                   :placeholder="$t('login.work_email_placeholder')"
                   qid="input-login-email"
                   @input="emailInvalid = false"
            >
          </div>
          <div v-if="emailInvalid" class="lg-hint lg-hint--err">
            <font-awesome-icon icon="exclamation-circle"/>{{ $t('login.email_invalid') }}
          </div>
        </div>
        <button type="submit" class="lg-cta" qid="button-continue-login">
          {{ $t('login.continue_button') }} <font-awesome-icon icon="arrow-right"/>
        </button>
      </form>
      <div class="lg-or">{{ $t('system.or') }}</div>
      <div class="lg-sso">
        <button type="button" class="lg-sso__btn" qid="login-microsoft-button" @click="handleMicrosoftLogin">
          <font-awesome-icon :icon="['fab', 'microsoft']"/>{{ $t('login.microsoft_short') }}
        </button>
        <button type="button" class="lg-sso__btn" qid="login-google-button" @click="handleGoogleLogin">
          <font-awesome-icon :icon="['fab', 'google']"/>{{ $t('login.google_short') }}
        </button>
      </div>
      <p class="lg-foot">{{ $t('login.no_account') }}</p>
    </template>

    <!-- 2. Password -->
    <template v-if="step === 'password'">
      <h1 class="lg-title">{{ $t('login.password_title') }}</h1>
      <div class="lg-who">
        <div class="lg-who__avatar">{{ initials }}</div>
        <div class="lg-who__email">{{ form.email }}</div>
        <a href="#" @click.prevent="changeEmail" qid="login-change-email">{{ $t('login.change_email') }}</a>
      </div>
      <form @submit.prevent="onSubmit" qid="login-form">
        <!-- Hidden username field so password managers save the email with the password -->
        <input type="email" :value="form.email" autocomplete="username" class="d-none" tabindex="-1" aria-hidden="true">
        <div class="lg-field">
          <label for="lg-password">
            {{ $t('login.password_label') }}
            <a href="#" @click.prevent="openForgotPassword" data-qid="show-forgot-password-link" qid="show-forgot-password-link">
              {{ $t('forgot_password.link') }}
            </a>
          </label>
          <div class="lg-input" :class="{ 'lg-input--err': error === 'invalid' }">
            <font-awesome-icon icon="lock"/>
            <input id="lg-password"
                   ref="password"
                   :type="showPassword ? 'text' : 'password'"
                   v-model="form.password"
                   autocomplete="current-password"
                   required
                   qid="input-login-password"
                   @input="error = error === 'invalid' ? '' : error"
            >
            <button type="button"
                    class="lg-input__eye"
                    @click="showPassword = !showPassword"
                    :aria-label="showPassword ? $t('login.hide_password') : $t('login.show_password')"
                    :title="showPassword ? $t('login.hide_password') : $t('login.show_password')"
            >
              <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'"/>
            </button>
          </div>
          <div v-if="error === 'invalid'" class="lg-hint lg-hint--err" qid="login-form-error">
            <font-awesome-icon icon="exclamation-circle"/>{{ $t('login.wrong_credentials') }}
          </div>
        </div>

        <div v-if="error === 'throttled'" class="lg-lock" qid="login-form-error">
          <div class="lg-lock__timer" :style="{ '--lg-lock-progress': lockProgress }">
            <b>{{ lockTime }}</b>
          </div>
          <div>
            <div class="lg-lock__title">{{ $t('login.throttled_title') }}</div>
            <div class="lg-lock__text">{{ $t('login.throttled_text') }}</div>
          </div>
        </div>

        <div v-if="error === 'unverified'" class="lg-alert lg-alert--amber" qid="login-form-error">
          <font-awesome-icon icon="envelope"/>
          <div>
            <b>{{ $t('login.unverified_title') }}</b>
            {{ $t('login.unverified_text') }}
            <a href="#" @click.prevent="resendEmail()">{{ $t('login.resend_link') }}</a>
          </div>
        </div>

        <div v-if="genericError" class="lg-alert lg-alert--red" qid="login-form-error">
          <font-awesome-icon icon="exclamation-circle"/><div>{{ genericError }}</div>
        </div>

        <button type="submit"
                class="lg-cta"
                :disabled="buttonDisabled || error === 'throttled'"
                qid="button-submit-login"
        >
          <span v-if="buttonDisabled" class="lg-spin"></span>
          {{ buttonDisabled ? $t('login.signing_in') : $t('login.title') }}
        </button>
      </form>
      <p class="lg-foot">
        {{ $t('login.not_you') }}
        <a href="#" @click.prevent="changeEmail">{{ $t('login.use_another_account') }}</a>
      </p>
    </template>

    <!-- 3. Single sign-on -->
    <template v-if="step === 'sso'">
      <h1 class="lg-title">{{ $t('login.title') }}</h1>
      <div v-if="form.email" class="lg-who">
        <div class="lg-who__avatar">{{ initials }}</div>
        <div class="lg-who__email">{{ form.email }}</div>
        <a href="#" @click.prevent="changeEmail">{{ $t('login.change_email') }}</a>
      </div>
      <div class="lg-org">
        <div class="lg-org__logo" :class="'lg-org__logo--' + ssoProvider">
          <font-awesome-icon :icon="['fab', ssoProvider]"/>
        </div>
        <div class="lg-org__title">
          {{ ssoFromHint ? $t('login.sso_last_' + ssoProvider) : $t('login.sso_title_' + ssoProvider) }}
        </div>
        <div v-if="!ssoFromHint" class="lg-org__text">{{ $t('login.sso_text') }}</div>
      </div>
      <button type="button"
              class="lg-cta"
              :qid="'login-' + ssoProvider + '-button'"
              @click="ssoProvider === 'microsoft' ? handleMicrosoftLogin() : handleGoogleLogin()"
      >
        <font-awesome-icon :icon="['fab', ssoProvider]"/>
        {{ $t('login.sso_continue_' + ssoProvider) }}
      </button>
      <p class="lg-foot">
        <a v-if="ssoFromHint" href="#" @click.prevent="usePasswordInstead">{{ $t('login.sso_use_password') }}</a>
        <template v-else>{{ $t('login.sso_help') }}</template>
      </p>
    </template>

    <!-- 4. Forgot password -->
    <template v-if="step === 'forgot'">
      <a href="#" class="lg-back" @click.prevent="backToSignIn" data-qid="go-to-login-link">
        <font-awesome-icon icon="chevron-left"/>{{ $t('forgot_password.back_to_sign_in') }}
      </a>
      <h1 class="lg-title">{{ $t('forgot_password.reset_title') }}</h1>
      <p class="lg-sub">{{ $t('forgot_password.reset_text') }}</p>
      <div v-if="forgotPassForm.wrongCredentials" class="lg-alert lg-alert--red" qid="request-pass-error">
        <font-awesome-icon icon="exclamation-circle"/><div>{{ forgotPassForm.errorMessage }}</div>
      </div>
      <form @submit.prevent="SubmitForgotPass">
        <div class="lg-field">
          <label for="lg-forgot-email">{{ $t('login.work_email_label') }}</label>
          <div class="lg-input">
            <font-awesome-icon icon="envelope"/>
            <input id="lg-forgot-email"
                   type="email"
                   v-model.trim="forgotPassForm.email"
                   autocomplete="username"
                   required
                   :placeholder="$t('login.work_email_placeholder')"
                   qid="input-requestPass-email"
            >
          </div>
        </div>
        <button type="submit" class="lg-cta" :disabled="buttonDisabled" qid="forgot-password-button">
          <span v-if="buttonDisabled" class="lg-spin"></span>
          {{ buttonDisabled ? $t('forgot_password.sending') : $t('forgot_password.send_link_button') }}
        </button>
      </form>
    </template>

    <!-- 5. Reset link sent -->
    <template v-if="step === 'sent'">
      <div class="lg-sent">
        <div class="lg-sent__icon"><font-awesome-icon icon="envelope"/></div>
        <h1 class="lg-title">{{ $t('forgot_password.sent_title') }}</h1>
        <p class="lg-sub">{{ $t('forgot_password.sent_text', { email: forgotPassForm.email }) }}</p>
      </div>
      <button type="button" class="lg-cta" @click="backToSignIn" qid="go-to-login-link">
        {{ $t('forgot_password.back_to_sign_in') }}
      </button>
      <p class="lg-foot">
        {{ $t('forgot_password.sent_help') }}
        <a href="#" @click.prevent="SubmitForgotPass">{{ $t('login.resend_link') }}</a>
      </p>
    </template>
  </auth-layout>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import AuthLayout from '@/components/auth/AuthLayout.vue'
import {landingPage} from '@/util/landingPage.js'

// Remembers the last email and how it signed in, so returning SSO users skip the password step.
// Kept in the browser only: the server never says how an email signs in before a correct password.
const HINT_KEY = 'login_hint';

export default {
  components: {
    AuthLayout
  },
  data: function() {
    return {
      step: 'email',
      form: {
        email: '',
        password: '',
      },
      forgotPassForm: {
        email: '',
        wrongCredentials: false,
        errorMessage: ''
      },
      emailInvalid: false,
      showPassword: false,
      // '', 'invalid', 'throttled' or 'unverified'
      error: '',
      genericError: '',
      ssoProvider: 'microsoft',
      ssoFromHint: false,
      lockSeconds: 0,
      lockTotal: 0,
      lockTimer: null,
      buttonDisabled: false,
      showResendEmailSuccessMessage: false,
      showTokenExpired: false,
      successMessage: '',
    }
  },
  computed: {
    getHistoryPage() {
      return this.$store.state.historyPage
    },
    initials() {
      const name = this.form.email.split('@')[0] || '';
      const parts = name.split(/[._-]+/).filter(Boolean);
      return ((parts[0] || '').charAt(0) + (parts[1] || '').charAt(0)).toUpperCase() || '?';
    },
    lockTime() {
      const minutes = Math.floor(this.lockSeconds / 60);
      const seconds = this.lockSeconds % 60;
      return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
    },
    lockProgress() {
      return this.lockTotal ? (this.lockSeconds / this.lockTotal * 100) + '%' : '0%';
    }
  },
  methods: {
    readHint() {
      try {
        return JSON.parse(localStorage.getItem(HINT_KEY)) || {};
      } catch (e) {
        return {};
      }
    },
    saveHint(method) {
      if (this.form.email) {
        localStorage.setItem(HINT_KEY, JSON.stringify({ email: this.form.email.toLowerCase(), method: method }));
      }
    },
    clearMessages() {
      this.error = '';
      this.genericError = '';
      this.successMessage = '';
      this.showTokenExpired = false;
      this.showResendEmailSuccessMessage = false;
    },
    focus(ref) {
      this.$nextTick(() => {
        if (this.$refs[ref]) {
          this.$refs[ref].focus();
        }
      });
    },
    continueWithEmail() {
      this.clearMessages();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.emailInvalid = true;
        this.focus('email');
        return;
      }

      const hint = this.readHint();
      if (hint.email === this.form.email.toLowerCase() && (hint.method === 'microsoft' || hint.method === 'google')) {
        this.ssoProvider = hint.method;
        this.ssoFromHint = true;
        this.step = 'sso';
        return;
      }

      this.step = 'password';
      this.focus('password');
    },
    changeEmail() {
      this.clearMessages();
      this.stopLockTimer();
      this.form.password = '';
      this.step = 'email';
      this.focus('email');
    },
    usePasswordInstead() {
      this.clearMessages();
      this.step = 'password';
      this.focus('password');
    },
    openForgotPassword() {
      this.clearMessages();
      this.forgotPassForm.email = this.forgotPassForm.email || this.form.email;
      this.forgotPassForm.wrongCredentials = false;
      this.step = 'forgot';
    },
    backToSignIn() {
      this.clearMessages();
      this.step = this.form.email ? 'password' : 'email';
      this.focus(this.step);
    },
    startLockTimer(seconds) {
      this.stopLockTimer();
      this.error = 'throttled';
      this.lockSeconds = seconds;
      this.lockTotal = seconds;
      this.lockTimer = setInterval(() => {
        this.lockSeconds--;
        if (this.lockSeconds <= 0) {
          this.stopLockTimer();
          this.error = '';
        }
      }, 1000);
    },
    stopLockTimer() {
      if (this.lockTimer) {
        clearInterval(this.lockTimer);
        this.lockTimer = null;
      }
    },
    SubmitForgotPass() {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      let self = this;

      piincHttp.post('password/email', { email: this.forgotPassForm.email }).then(function() {
        self.step = 'sent';
        self.forgotPassForm.wrongCredentials = false;
        self.buttonDisabled = false;
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.step = 'forgot';
          self.forgotPassForm.wrongCredentials = true;
          self.forgotPassForm.errorMessage = error.data.message;
        }
      })
    },
    resendEmail() {
      // Keep the reference in a variable
      let self = this;

      self.showTokenExpired = false;
      self.successMessage = '';

      piincHttp.post('/email/resend/' + this.form.email).then(function() {
        self.showResendEmailSuccessMessage = true;
        self.error = '';
      }, function(error) {
        if (error.status !== 200) {
          self.genericError = error.data.message;
        }
      })
    },
    onSubmit() {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      let self = this;

      this.clearMessages();

      piincHttp.post('login', this.form).then(function(response) {
        self.saveHint('password');
        localStorage.setItem('token', response.data.token);
        self.$store.commit('storeUser', response.data.user);
        piincHttp.defaults.headers.common['Authorization'] = localStorage.getItem('token');

        let page = landingPage(self.getHistoryPage, response.data.user.isAdmin);
        // The return page is used once; the next sign-in starts from the default page again
        self.$store.commit('storeHistoryPage', '');
        window.location.href = page;
      }, function(error) {
        self.buttonDisabled = false;
        const reason = error.data && error.data.reason;

        if (error.status === 429) {
          self.startLockTimer(error.data.retry_after || 60);
        } else if (reason === 'sso_microsoft' || reason === 'sso_google') {
          self.ssoProvider = reason === 'sso_microsoft' ? 'microsoft' : 'google';
          self.ssoFromHint = false;
          self.saveHint(self.ssoProvider);
          self.form.password = '';
          self.step = 'sso';
        } else if (reason === 'unverified') {
          self.error = 'unverified';
        } else if (reason === 'invalid') {
          self.error = 'invalid';
          self.focus('password');
        } else {
          self.genericError = (error.data && error.data.message) || self.$t('login.error_message');
        }
      })
    },
    handleMicrosoftLogin() {
      this.saveHint('microsoft');
      window.location.href = '/api/auth/azure';
    },
    handleGoogleLogin() {
      this.saveHint('google');
      window.location.href = '/api/auth/google';
    }
  },
  // Lifecycle hooks
  created() {
    if (this.$route.params.error) {
      this.genericError = this.$t('login.error_message')
    }

    if (this.$route.params.success) {
      this.successMessage = this.$t('login.success_message')
    }

    if (this.$route.params.expired) {
      this.showTokenExpired = true;
    }

    this.form.email = this.readHint().email || '';

    // Came back from Microsoft/Google, but the account only allows the other one
    if (this.$route.query.allowed === 'google' || this.$route.query.allowed === 'microsoft') {
      this.ssoProvider = this.$route.query.allowed;
      this.ssoFromHint = false;
      this.saveHint(this.ssoProvider);
      this.step = 'sso';
    }

    if (this.$route.query.reset && this.$route.query.reset === 'true') {
      this.step = 'forgot';
    }
  },
  mounted() {
    if (this.step === 'email') {
      this.focus('email');
    }
  },
  beforeDestroy() {
    this.stopLockTimer();
  }
}
</script>
